import { useState } from 'react';

import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import {
  IconButton,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Box,
  Divider,
} from '@mui/material';

import { InputClearButton } from 'components';
import { countryOptions } from 'helpers';
import {
  OptionalCallback,
  OrganizationTableGlobalFilterOptions,
  OrganizationRelationshipOption,
  AssignedOption,
  SortOptions,
} from 'types';

import { SidebarWrapper } from './styledComponents';

// used for input state
type Selections = {
  organization_name: string;
  type: OrganizationRelationshipOption | '';
  assigned: AssignedOption | '';
  sort_order: SortOptions | '';
  country: string;
};

// used for init / the reset
export const defaultSelections: Selections = {
  organization_name: '',
  type: '',
  assigned: '',
  sort_order: '',
  country: '',
};

interface SidebarProps {
  setFilters: (
    arg: OptionalCallback<OrganizationTableGlobalFilterOptions>,
  ) => void;
  filters?: OrganizationTableGlobalFilterOptions;
}

export const OrganizationTableSidebar: React.FC<SidebarProps> = ({
  setFilters,
  filters,
}) => {
  const { name, sort_order, type, assigned, country } = filters ?? {};
  const [expanded, setExpanded] = useState(true);

  return (
    <SidebarWrapper expanded={expanded}>
      <header>
        {expanded && <Typography variant='h5'>Filter</Typography>}
        <IconButton
          onClick={() => setExpanded(!expanded)}
          key='expand'
          role='button'
          aria-label='expand and close filter'
          aria-expanded
        >
          {expanded ? (
            <KeyboardArrowLeft fontSize='medium' />
          ) : (
            <KeyboardArrowRight fontSize='medium' />
          )}
        </IconButton>
      </header>

      {expanded && (
        <section>
          <TextField
            fullWidth
            size='small'
            label='Employer'
            value={name ?? ''}
            onChange={({ target: { value } }) => {
              setFilters((prev) => ({
                ...prev,
                name: value ? value : undefined,
              }));
            }}
          />
          <FormControl fullWidth size='small'>
            <InputLabel id='organization-relationship'>
              Employer Type
            </InputLabel>
            <Select
              labelId='organization-relationship'
              value={type || ''}
              label='Employer Type'
              onChange={({ target: { value: type } }) =>
                setFilters((prev) => ({
                  ...prev,
                  ...{ type: type as OrganizationRelationshipOption },
                }))
              }
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!type}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, type: undefined }))
                  }
                />
              }
            >
              <MenuItem value='top'>Organization</MenuItem>
              <MenuItem value='associated'>Associated Entity</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            id='country-select'
            options={countryOptions}
            groupBy={(option) =>
              ['AF', 'US'].includes(option.value) ? '' : '--'
            }
            renderGroup={(params) => (
              <>
                <Divider key={params.key} />
                {params.children}
              </>
            )}
            isOptionEqualToValue={(option, value) => {
              return option.value === value.value;
            }}
            value={
              countryOptions.find(
                (countryOption) => countryOption.value === country,
              ) || { value: '', label: '' }
            }
            autoHighlight
            onChange={(_, value) =>
              setFilters((prev) => ({
                ...prev,
                country: value?.value ? value.value : undefined,
              }))
            }
            getOptionLabel={(option) => option.label}
            renderOption={(props, option) => (
              <Box
                component='li'
                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                {...props}
              >
                <img
                  loading='lazy'
                  width='20'
                  src={`https://flagcdn.com/w20/${option.value.toLowerCase()}.png`}
                  srcSet={`https://flagcdn.com/w40/${option.value.toLowerCase()}.png 2x`}
                  alt=''
                />
                {option.label} ({option.value})
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                label='Employer Country'
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'select country', // disable autocomplete and autofill
                  endAdornment: (
                    <InputClearButton
                      buttonProps={{ 'aria-label': 'Clear selection' }}
                      visible={!!type}
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          country: undefined,
                        }))
                      }
                    />
                  ),
                }}
              />
            )}
          />
          <FormControl fullWidth size='small'>
            <InputLabel id='assigned-to-employer'>
              Assigned to Employer?
            </InputLabel>
            <Select
              labelId='assigned-to-employer'
              value={assigned || ''}
              label='Assigned to Employer?'
              onChange={({ target: { value: assigned } }) =>
                setFilters((prev) => ({
                  ...prev,
                  ...{ assigned: assigned as AssignedOption },
                }))
              }
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!assigned}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, assigned: undefined }))
                  }
                />
              }
            >
              <MenuItem value='assigned'>Assigned</MenuItem>
              <MenuItem value='unassigned'>Unassigned</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size='small'>
            <InputLabel id='Sort-By-Candidate-Count'>
              Sort By Candidate Count
            </InputLabel>
            <Select
              labelId='organization-sort'
              value={sort_order || ''}
              label='Sort By Candidate Count'
              onChange={({ target: { value: sort_order } }) =>
                setFilters((prev) => ({
                  ...prev,
                  ...{ sort_order: sort_order as SortOptions },
                }))
              }
              endAdornment={
                <InputClearButton
                  buttonProps={{ 'aria-label': 'Clear selection' }}
                  visible={!!sort_order}
                  onClick={() =>
                    setFilters((prev) => ({ ...prev, sort_order: undefined }))
                  }
                />
              }
            >
              <MenuItem value='ascending'>Ascending</MenuItem>
              <MenuItem value='descending'>Descending</MenuItem>
            </Select>
          </FormControl>
        </section>
      )}
    </SidebarWrapper>
  );
};
